import { Box, Chip, Dialog, DialogContent, Fade, Paper, Slide, Slider, Stack, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { Loader } from '@treedis/tour-loader';
import { useCallback, useRef, useState } from 'react';
import ReactPlayer from 'react-player';

type Props = {
  videoId: string;
  onClose: () => void;
};

const INIT_SETTINGS = {
  url: '',
  playing: false,
  volume: 1,
  played: 0,
  duration: 0,
};

function formatDuration(value: number) {
  const minute = Math.floor(value / 60);
  const secondLeft = value - minute * 60;
  return `${minute}:${secondLeft < 10 ? `0${secondLeft}` : secondLeft}`;
}

export default function MondelezTabletVideo({ videoId, onClose }: Props) {
  const [videoReady, setVideoReady] = useState(false);
  const containerRef = useRef(null);
  const playerRef = useRef(null);

  const [player, setPlayer] = useState({
    ...INIT_SETTINGS,
    url: `https://mondelez-cdn.enterprise.treedis.com/videos/${videoId}`,
  });

  const handleSeek = useCallback(
    (value: number) => {
      const newValue = player.played + value;

      if (newValue < 0) {
        playerRef?.current?.seekTo(0);
        setPlayer((prev) => ({ ...prev, played: 0 }));
      }
      if (newValue > player.duration) {
        playerRef?.current?.seekTo(player.duration);
        setPlayer((prev) => ({ ...prev, played: player.duration }));
      } else if (newValue > 0 && newValue < player.duration) {
        playerRef?.current?.seekTo(newValue);
        setPlayer((prev) => ({ ...prev, played: newValue }));
      }
    },
    [player.duration, player.played],
  );

  return (
    <Dialog
      open
      fullScreen
      onClose={onClose}
      PaperProps={{
        sx: {
          background: 'rgba(127,127,127,0.9)',
          backdropFilter: 'blur(0)',
        },
      }}
    >
      <DialogContent
        ref={containerRef}
        sx={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          alignItems: 'end',
          overflow: 'hidden',
          padding: 0,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 999,
            color: '#fff',
          }}
        >
          <i className="fa-solid fa-times" />
        </IconButton>

        <Slide timeout={1500} in direction="up" container={containerRef.current}>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 'auto',
              maxHeight: '100vh',
            }}
          >
            <Box
              component="img"
              src="/images/ipad_and_hands.png"
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '100vh',
                objectFit: 'contain',
                visibility: 'visible !important',
                zIndex: 2,
                position: 'relative',
                marginBottom: '-7px',
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-48%, -56%)',
                width: 'min(55%, 110vh)',
                height: '80%',
                background: 'white',
                zIndex: 1,
              }}
            >
              {/* Loader */}
              {!videoReady && (
                <Box
                  sx={{
                    zIndex: 99,
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    background: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Loader color="#2055FF" />
                </Box>
              )}
              <Fade in timeout={1000}>
                <Box width="100%" height="100%">
                  <ReactPlayer
                    ref={playerRef}
                    width="100%"
                    height="100%"
                    url={player.url}
                    volume={player.volume}
                    playing={player.playing}
                    onReady={() => {
                      setTimeout(() => {
                        setVideoReady(true);
                      }, 500);
                    }}
                    onEnded={() =>
                      setTimeout(() => {
                        onClose();
                      }, 500)
                    }
                    onProgress={(progress) => {
                      setPlayer((prev) => ({ ...prev, played: Math.floor(progress.playedSeconds) }));
                    }}
                    onDuration={(duration) => {
                      setPlayer((prev) => ({ ...prev, duration: Math.floor(duration) }));
                    }}
                  />
                </Box>
              </Fade>
            </Box>
            {!player.playing && videoReady && (
              <IconButton
                sx={{
                  position: 'absolute',
                  top: '40%',
                  left: '50%',
                  zIndex: 5,
                  width: '50px',
                  height: '50px',
                  color: 'rgba(0,0,0,0.8)',
                  transform: 'translateX(-50%)',

                  '&>i': {
                    fontSize: '7vw',
                  },
                }}
                onClick={() => setPlayer((prev) => ({ ...prev, playing: !player.playing }))}
              >
                <i className="fa-solid fa-circle-play" />
              </IconButton>
            )}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{
                position: 'absolute',
                bottom: '2%',
                left: '50%',
                transform: 'translateX(-50%)',
                borderRadius: '10px',
                background: 'rgba(255,255,255,0.8)',
                px: 1,
              }}
              zIndex={5}
            >
              <IconButton onClick={() => setPlayer((prev) => ({ ...prev, playing: !player.playing }))}>
                <i className={`fa-solid ${player.playing ? 'fa-pause' : 'fa-play'}`} />
              </IconButton>
              <Tooltip title="-10s">
                <IconButton onClick={() => handleSeek(-10)} disabled={player.played === 0}>
                  <i className="fa-solid fa-undo" />
                </IconButton>
              </Tooltip>
              <Tooltip title="+10s">
                <IconButton onClick={() => handleSeek(10)} disabled={player.played === 0}>
                  <i className="fa-solid fa-redo" />
                </IconButton>
              </Tooltip>
              <Tooltip
                enterDelay={100}
                placement="right"
                arrow={false}
                components={{
                  Tooltip: Paper,
                }}
                componentsProps={{
                  tooltip: {
                    sx: {
                      background: 'rgba(255,255,255,0.8)',
                      mr: 2,
                    },
                  },
                }}
                title={
                  <Slider
                    size="small"
                    value={player.volume * 100}
                    onChange={(e, value) => {
                      if (Number(value)) {
                        setPlayer((prev) => ({ ...prev, volume: Number(value) / 100 }));
                      }
                    }}
                    min={0}
                    max={100}
                    sx={{ width: '200px' }}
                  />
                }
              >
                <IconButton onClick={() => setPlayer((prev) => ({ ...prev, volume: player.volume > 0 ? 0 : 1 }))}>
                  <i className={`fa-solid ${player.volume === 0 ? 'fa-volume-slash' : 'fa-volume'}`} />
                </IconButton>
              </Tooltip>
              <Chip label={`${formatDuration(player.played)} / ${formatDuration(player.duration)}`} />
            </Stack>
          </Box>
        </Slide>
      </DialogContent>
    </Dialog>
  );
}
